/**
 * Format a date string to localized format
 */
export function formatDate(date: string | Date, locale = 'fr-FR'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date, locale = 'fr-FR'): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

  return formatDate(dateObj, locale);
}

/**
 * Format conversation timestamp:
 * - today: HH:mm
 * - yesterday: localized label
 * - within the week: localized weekday (short)
 * - older: full date (DD/MM/YYYY)
 */
export function formatConversationTimestamp(
  date: string | Date,
  locale = 'fr-FR',
  yesterdayLabel = 'Yesterday'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday);
  startOfWeek.setDate(startOfWeek.getDate() - 7);

  // Today: show time
  if (dateObj >= startOfToday) {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  }

  // Yesterday: show "Hier" or "Yesterday"
  if (dateObj >= startOfYesterday && dateObj < startOfToday) {
    return yesterdayLabel;
  }

  // Within the week: show abbreviated weekday (Lun, Mar, Mer, Jeu, Ven, Sam, Dim)
  if (dateObj >= startOfWeek) {
    const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(dateObj);
    // Capitalize first letter (lun. -> Lun., mar. -> Mar., etc.)
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }

  // Older than a week: show full date (DD/MM/YYYY)
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
}

/**
 * Format currency
 */
export function formatCurrency(amount: number, currency = 'EUR', locale = 'fr-FR'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format price with abbreviations (M€) for large amounts
 * Always uses dot (.) as decimal separator with thousand separators
 */
export function formatPriceWithAbbr(value: number): string {
  if (value >= 1000000) {
    const millions = (value / 1000000).toFixed(2);
    return `€${millions} M`;
  }
  // Add thousand separators while keeping dot as decimal separator
  const parts = value.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `€${parts.join('.')}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}
